/**
 * TutorialOverlay Component (AC-671)
 *
 * Main controller component that orchestrates the tutorial experience.
 * Manages spotlight and tooltip positioning, step completion detection,
 * and navigation event listeners.
 *
 * Technical Design Reference: docs/technical/TDD-AC-661-tutorial-system.md
 *
 * Features:
 * - Reads state from tutorialStore
 * - Renders TutorialSpotlight and TutorialTooltip
 * - Finds target element using step's targetSelector
 * - Tracks target element position (handles resize/scroll)
 * - Listens for step completion events (navigation, clicks)
 * - Shows TutorialCompletionModal when tutorial completes
 * - Cleans up event listeners on unmount
 *
 * @see AC-671 - TutorialOverlay controller component
 * @see AC-661 - Epic: Interactive Tutorial System
 */

import React, { useEffect, useLayoutEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTutorialStore } from '../../store/tutorialStore';
import { useUIStore } from '../../store';
import { getTutorialStep, TUTORIAL_WORKFLOW_NAMES } from '../../config/tutorialConfig';
import apiService from '../../services/api';
import { TutorialStep } from '../../types/tutorial';
import { useIsMobile } from '../../hooks/useIsMobile';
import TutorialSpotlight from './TutorialSpotlight';
import TutorialTooltip from './TutorialTooltip';
import TutorialCompletionModal from './TutorialCompletionModal';

/**
 * Check if the current pathname matches the expected route pattern.
 * Supports dynamic segments like ':id' which match any value.
 * Also supports partial matching with endsWith for nested routes.
 */
function matchesExpectedRoute(pathname: string, expectedRoute: string): boolean {
  // No expected route means any route is valid
  if (!expectedRoute) return true;

  // Exact match
  if (pathname === expectedRoute) return true;

  // Partial match (e.g., '/workflows' matches '/projects/123/workflows')
  if (pathname.endsWith(expectedRoute)) return true;

  // Pattern match with dynamic segments (e.g., '/workflows/:id')
  // Filter out empty strings from split to handle leading slashes correctly
  const patternParts = expectedRoute.split('/').filter(p => p !== '');
  const pathParts = pathname.split('/').filter(p => p !== '');

  // Check if path starts with the pattern (sub-route match)
  // E.g., pattern ['projects', ':id'] should match ['projects', '5721', 'integrations']
  if (pathParts.length >= patternParts.length) {
    const prefixParts = pathParts.slice(0, patternParts.length);
    const prefixMatch = patternParts.every((pattern, i) => {
      if (pattern.startsWith(':')) return true;
      return pattern === prefixParts[i];
    });
    if (prefixMatch) return true;
  }

  // Check if path ends with the pattern (for nested routes)
  // E.g., pattern ['orchestrations', ':id'] should match path ending with ['orchestrations', '456']
  const startIndex = pathParts.length - patternParts.length;
  if (startIndex >= 0) {
    const relevantParts = pathParts.slice(startIndex);
    return patternParts.every((pattern, i) => {
      if (pattern.startsWith(':')) return true; // Dynamic segment matches anything
      return pattern === relevantParts[i];
    });
  }

  return false;
}

/**
 * TutorialOverlay - Main tutorial controller component.
 */
const TutorialOverlay: React.FC = () => {
  // Tutorial store state and actions
  const {
    status,
    currentStep,
    totalSteps,
    activeTutorialId,
    showCompletionModal,
    contextData: storedContextData,
    advanceStep,
    skipStep,
    dismissTutorial,
    setShowCompletionModal,
    updateTutorialContext,
  } = useTutorialStore();

  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { setMobileDrawerOpen } = useUIStore();

  // Target element rect for spotlight and tooltip positioning
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const targetRectRef = useRef<DOMRect | null>(null);

  // Smart setter that only triggers re-render when DOMRect values actually change
  const updateTargetRect = useCallback((newRect: DOMRect | null) => {
    const prev = targetRectRef.current;
    if (newRect === null && prev === null) return;
    if (newRect && prev &&
        newRect.top === prev.top && newRect.left === prev.left &&
        newRect.width === prev.width && newRect.height === prev.height) return;
    targetRectRef.current = newRect;
    setTargetRect(newRect);
  }, []);

  // Track the current step configuration (uses active tutorial's step array)
  const currentStepConfig = currentStep > 0 ? getTutorialStep(currentStep, activeTutorialId) : undefined;

  /**
   * Auto-open sidebar drawer on mobile when step targets a nav item.
   */
  useEffect(() => {
    if (!isMobile || !currentStepConfig) return;
    if (currentStepConfig.targetSelector.startsWith('[data-tutorial="nav-')) {
      setMobileDrawerOpen(true);
    }
  }, [currentStep, isMobile, currentStepConfig, setMobileDrawerOpen]);

  /**
   * Auto-expand the Configuration sidebar group when a step targets an item
   * inside it (e.g., nav-integrations). Without this, the target element
   * doesn't exist in the DOM and the spotlight can't find it.
   */
  useEffect(() => {
    if (!currentStepConfig) return;
    const sel = currentStepConfig.targetSelector;

    // All nav items inside the Project sidebar group need it expanded
    const projectNavItems = ['nav-agents', 'nav-workflows', 'nav-integrations', 'nav-settings'];
    const isProjectNavItem = projectNavItems.some((item) => sel.includes(item));

    if (isProjectNavItem) {
      // Read directly from store to avoid stale closure issues
      const { sidebarGroups: groups } = useUIStore.getState();
      const updates: Record<string, boolean> = {};
      // Expand the project group if collapsed
      if (groups.project === false) updates.project = true;
      // Also expand config sub-group for items inside it (integrations, settings)
      if (sel.includes('nav-integrations') || sel.includes('nav-settings')) {
        if (!groups.config) updates.config = true;
      }
      if (Object.keys(updates).length > 0) {
        useUIStore.setState({
          sidebarGroups: { ...groups, ...updates },
        });
      }
    }
  }, [currentStep, currentStepConfig]);

  // React Router location for navigation detection
  const location = useLocation();

  // Track the last captured context to avoid redundant API calls
  const lastCapturedContextRef = useRef<string>('');

  /**
   * Capture project/workflow IDs from the URL during an active tutorial.
   * Stores them as context_data so the tutorial can resume to the correct page.
   */
  useEffect(() => {
    if (status !== 'in_progress') return;

    const path = location.pathname;
    const contextData: Record<string, unknown> = {};

    // Match /projects/:id or /projects/:id/...
    const operationMatch = path.match(/\/projects\/(\d+)/);
    if (operationMatch) {
      contextData.operation_id = parseInt(operationMatch[1], 10);
    }

    // Match /workflows/:id (the workflow editor route)
    const workflowMatch = path.match(/\/workflows\/(\d+)/);
    if (workflowMatch) {
      contextData.workflow_id = parseInt(workflowMatch[1], 10);
    }

    // Only update if we have context and it changed
    const contextKey = JSON.stringify(contextData);
    if (Object.keys(contextData).length > 0 && contextKey !== lastCapturedContextRef.current) {
      lastCapturedContextRef.current = contextKey;
      updateTutorialContext(contextData);
    }
  }, [status, location.pathname, updateTutorialContext]);

  // Check if user is on the wrong route for this step.
  // Suppress for 500ms after a step change to allow navigation from the previous
  // step's click to settle before showing "Navigate to Continue".
  const [stepChangeGrace, setStepChangeGrace] = useState(false);
  useEffect(() => {
    setStepChangeGrace(true);
    const timer = setTimeout(() => setStepChangeGrace(false), 500);
    return () => clearTimeout(timer);
  }, [currentStep]);

  const isOnWrongRoute = useMemo(() => {
    if (stepChangeGrace) return false;
    if (!currentStepConfig?.expectedRoute) return false;
    return !matchesExpectedRoute(location.pathname, currentStepConfig.expectedRoute);
  }, [stepChangeGrace, currentStepConfig, location.pathname]);

  // Ref to prevent repeated auto-navigation attempts
  const autoNavAttemptedRef = useRef(false);

  // Reset auto-nav flag when step changes
  useEffect(() => {
    autoNavAttemptedRef.current = false;
  }, [currentStep]);

  // Reset context tracking when tutorial changes
  useEffect(() => {
    lastCapturedContextRef.current = '';
    autoNavAttemptedRef.current = false;
  }, [activeTutorialId]);

  /**
   * Auto-navigate to the correct route when user is on wrong page.
   * Uses stored context data (workflow_id, operation_id) to resolve dynamic routes.
   * Falls back to looking up workflow from the project when workflow_id is missing.
   */
  useEffect(() => {
    if (!isOnWrongRoute || !currentStepConfig?.expectedRoute || autoNavAttemptedRef.current) return;
    if (status !== 'in_progress') return;
    // Don't auto-navigate from the bootcamp page — the resume handler manages navigation
    if (location.pathname.startsWith('/bootcamp')) return;

    const route = currentStepConfig.expectedRoute;
    const ctx = storedContextData ?? {};

    if (route.includes(':id')) {
      if (route.includes('workflows')) {
        // Always validate workflow_id matches the current tutorial by looking up by name
        if (ctx.operation_id) {
          autoNavAttemptedRef.current = true;
          const expectedName = TUTORIAL_WORKFLOW_NAMES[activeTutorialId];
          apiService.getWorkflows(String(ctx.operation_id)).then(workflows => {
            const match = expectedName
              ? workflows.find(w => w.name === expectedName)
              : undefined;
            if (match) {
              updateTutorialContext({ workflow_id: match.orchestration_id });
              navigate(`/projects/${ctx.operation_id}/workflows/${match.orchestration_id}`);
            } else {
              // Workflow not found — navigate to project page so user can create it
              navigate(`/projects/${ctx.operation_id}`);
            }
          }).catch(() => { /* ignore lookup failure */ });
          return;
        }
      }
      if (route.includes('projects') && ctx.operation_id) {
        autoNavAttemptedRef.current = true;
        navigate(`/projects/${ctx.operation_id}`);
        return;
      }
    } else if (route === '/workflows' && ctx.operation_id) {
      // Workflows list page — navigate within the project
      autoNavAttemptedRef.current = true;
      navigate(`/projects/${ctx.operation_id}/workflows`);
    } else {
      // Static route — navigate directly
      autoNavAttemptedRef.current = true;
      navigate(route);
    }
  }, [isOnWrongRoute, currentStepConfig, status, navigate, storedContextData, updateTutorialContext]);

  // Create a redirect step when user is on wrong route (fallback if auto-nav can't resolve)
  const displayStepConfig: TutorialStep | undefined = useMemo(() => {
    if (!currentStepConfig) return undefined;
    if (!isOnWrongRoute) return currentStepConfig;
    // Don't show redirect message on bootcamp page — resume handler navigates from there
    if (location.pathname.startsWith('/bootcamp')) return undefined;

    // User is on wrong route - show redirect message
    // Extract a friendly route name, filtering out dynamic segments like ':id'
    const routeParts = currentStepConfig.expectedRoute?.split('/').filter(p => p && !p.startsWith(':')) || [];
    const routeName = routeParts[routeParts.length - 1] || 'the correct page';
    const friendlyRouteName = routeName.charAt(0).toUpperCase() + routeName.slice(1);
    return {
      ...currentStepConfig,
      title: 'Navigate to Continue',
      description: currentStepConfig.redirectMessage ||
        `To continue the tutorial, you need to be in the ${friendlyRouteName} section.\n\n` +
        'Use the sidebar navigation to get there, or click "Skip this step" to move on.',
      actionPrompt: `Navigate to ${friendlyRouteName} to continue.`,
      hideSpotlight: true, // Don't show spotlight when redirecting
      completionTrigger: {
        type: 'navigation' as const,
        value: currentStepConfig.expectedRoute || '',
      },
    };
  }, [currentStepConfig, isOnWrongRoute]);

  // Ref to track if we've already processed a step completion
  const stepCompletionRef = useRef(false);

  // Ref to track the previous step number to detect step transitions
  const previousStepRef = useRef<number>(currentStep);

  // Ref to track the path when the current step started
  // This ensures we only advance on navigation that happens AFTER the step began
  const stepStartPathRef = useRef<string>(location.pathname);

  /**
   * Find and track target element position.
   * Auto-scrolls to elements that are off-screen (e.g., sidebar items scrolled out of view).
   */
  const updateTargetPosition = useCallback(() => {
    if (!currentStepConfig) {
      updateTargetRect(null);
      return;
    }

    const targetElement = document.querySelector(currentStepConfig.targetSelector) as HTMLElement | null;
    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();

      // Auto-scroll to targets hidden in scrollable containers (e.g., sidebar nav).
      // Check both viewport visibility and whether the element is clipped by
      // an overflow container (the sidebar nav has overflow-y: auto).
      const isClippedInContainer = (() => {
        let parent = targetElement.parentElement;
        while (parent) {
          const style = getComputedStyle(parent);
          if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
            const parentRect = parent.getBoundingClientRect();
            if (rect.top < parentRect.top || rect.bottom > parentRect.bottom) {
              return true;
            }
            break;
          }
          parent = parent.parentElement;
        }
        return false;
      })();
      const isOffScreen = rect.top < 0 || rect.bottom > window.innerHeight || rect.left < 0 || rect.right > window.innerWidth;
      if (isOffScreen || isClippedInContainer) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Wait for smooth scroll to finish before re-reading rect
        setTimeout(() => {
          updateTargetRect(targetElement.getBoundingClientRect());
        }, 350);
        return;
      }

      updateTargetRect(rect);
    } else {
      updateTargetRect(null);
    }
  }, [currentStepConfig, updateTargetRect]);

  /**
   * Initial position update (synchronous via useLayoutEffect for test compatibility).
   */
  useLayoutEffect(() => {
    if (status !== 'in_progress') return;
    updateTargetPosition();
  }, [status, currentStep, updateTargetPosition]);

  /**
   * Update target position on resize, scroll, and orientation change.
   * On mobile, uses MutationObserver instead of setInterval for better performance.
   */
  useEffect(() => {
    if (status !== 'in_progress') return;

    // Update on window resize
    const handleResize = () => {
      updateTargetPosition();
    };

    // Update on scroll
    const handleScroll = () => {
      updateTargetPosition();
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('orientationchange', handleResize);

    let intervalId: ReturnType<typeof setInterval> | undefined;
    let observer: MutationObserver | undefined;

    if (isMobile) {
      // On mobile, use MutationObserver instead of setInterval for performance
      let rafId: number | undefined;
      const observerCallback = () => {
        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          updateTargetPosition();
        });
      };
      try {
        const obs = new MutationObserver(observerCallback);
        obs.observe(document.body, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'style'],
        });
        observer = obs;
      } catch {
        // MutationObserver construction may fail in test environments
      }
    } else {
      // On desktop, keep existing setInterval
      intervalId = setInterval(updateTargetPosition, 500);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('orientationchange', handleResize);
      if (intervalId) clearInterval(intervalId);
      if (observer) observer.disconnect();
    };
  }, [status, updateTargetPosition, isMobile]);

  /**
   * Handle navigation-based step completion.
   * Supports both exact path matching, suffix matching, and route patterns.
   * E.g., '/agents' will match '/projects/123/agents'
   * E.g., '/workflows/:id' will match '/projects/123/workflows/456'
   *
   * Advances when:
   * 1. User navigates to the expected path (path changed since step started)
   * 2. User is already on the expected path when step becomes active (auto-advance)
   */
  useEffect(() => {
    if (status !== 'in_progress' || !currentStepConfig) return;

    // Check if current step has navigation trigger
    if (currentStepConfig.completionTrigger.type === 'navigation') {
      const expectedPath = currentStepConfig.completionTrigger.value;

      // Check if current location matches expected path using pattern matching
      const currentPathMatches = matchesExpectedRoute(location.pathname, expectedPath);

      // Check if the path changed since the step started
      const pathChangedSinceStepStart = location.pathname !== stepStartPathRef.current;

      // Advance if path matches and we haven't already completed this step
      // This handles both: user navigating to the page, OR already being on the page
      const shouldAdvance = currentPathMatches && !stepCompletionRef.current;

      if (shouldAdvance) {
        // Mark as completing immediately to prevent duplicate triggers
        stepCompletionRef.current = true;

        // If user is already on the correct page (no path change), add a small delay
        // to allow the UI to settle and show the step briefly before advancing
        if (!pathChangedSinceStepStart) {
          const timeoutId = setTimeout(() => {
            advanceStep();
          }, 500); // Brief delay so user sees the step
          return () => {
            // Only reset if timeout was cancelled (component unmounted or deps changed)
            clearTimeout(timeoutId);
            // Don't reset stepCompletionRef here - it will be reset by step change effect
          };
        } else {
          // User navigated here - advance immediately
          advanceStep();
        }
      }
    }
  }, [status, currentStepConfig, location.pathname, advanceStep]);

  // Ref to track when the step last changed (for debouncing click detection)
  const stepChangeTimeRef = useRef<number>(Date.now());

  /**
   * Reset step completion tracking when step changes.
   * Captures the current path as the "baseline" for the new step.
   * This ensures we only advance on navigation that happens AFTER the step change.
   */
  useEffect(() => {
    // Only reset if the step actually changed (not on initial render with same step)
    if (previousStepRef.current !== currentStep) {
      stepCompletionRef.current = false;
      // Capture current path as baseline for the new step
      // This prevents immediate advancement if user is already on a matching page
      stepStartPathRef.current = location.pathname;
      previousStepRef.current = currentStep;
      // Track when the step changed for debouncing
      stepChangeTimeRef.current = Date.now();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);


  /**
   * Handle click-based step completion.
   * Includes debounce to prevent accidental triggers from previous step's click events.
   */
  useEffect(() => {
    if (status !== 'in_progress' || !currentStepConfig) return;

    // Check if current step has click trigger
    if (currentStepConfig.completionTrigger.type !== 'click') return;

    const targetSelector = currentStepConfig.completionTrigger.value;

    const handleClick = (event: MouseEvent) => {
      // Debounce: Ignore clicks that happen within 300ms of a step change
      // This prevents residual click events from the previous step from triggering
      const timeSinceStepChange = Date.now() - stepChangeTimeRef.current;
      if (timeSinceStepChange < 300) {
        return;
      }

      const target = event.target as HTMLElement;
      const matchesSelector = target.matches(targetSelector) ||
                             target.closest(targetSelector) !== null;

      if (matchesSelector && !stepCompletionRef.current) {
        stepCompletionRef.current = true;
        // Small delay to let the click action complete
        setTimeout(() => {
          advanceStep();
        }, 100);
      }
    };

    // Also listen for Enter key on inputs near the target button
    // (e.g., user presses Enter instead of clicking Submit)
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter') return;
      const timeSinceStepChange = Date.now() - stepChangeTimeRef.current;
      if (timeSinceStepChange < 300) return;

      // Check if the target button exists and the keypress is in a related input
      const targetEl = document.querySelector(targetSelector);
      if (!targetEl || stepCompletionRef.current) return;

      // Verify the Enter was pressed in an input that's a sibling/nearby the target button
      const active = document.activeElement;
      if (active?.tagName === 'INPUT' && active.closest('div')?.contains(targetEl)) {
        stepCompletionRef.current = true;
        setTimeout(() => {
          advanceStep();
        }, 100);
      }
    };

    // Add click listener with capture to catch events before they bubble
    document.addEventListener('click', handleClick, true);
    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [status, currentStepConfig, advanceStep]);

  /**
   * Handle skip button click.
   */
  const handleSkip = useCallback(() => {
    skipStep();
  }, [skipStep]);

  /**
   * Handle exit button click.
   */
  const handleExit = useCallback(() => {
    dismissTutorial();
  }, [dismissTutorial]);

  /**
   * Handle continue button click (for manual/info-only steps).
   */
  const handleContinue = useCallback(() => {
    advanceStep();
  }, [advanceStep]);

  /**
   * Handle completion modal close.
   */
  const handleCompletionClose = useCallback(() => {
    setShowCompletionModal(false);
  }, [setShowCompletionModal]);

  /**
   * Handle explore help from completion modal.
   * Navigates to the Bootcamp tutorials page.
   */
  const handleExploreHelp = useCallback(() => {
    setShowCompletionModal(false);
    navigate('/bootcamp/tutorials');
  }, [setShowCompletionModal, navigate]);

  // Show completion modal even when status is 'completed'
  // (advanceStep sets both status='completed' and showCompletionModal=true atomically)
  if (showCompletionModal) {
    return (
      <TutorialCompletionModal
        isOpen={showCompletionModal}
        onClose={handleCompletionClose}
        onExploreHelp={handleExploreHelp}
        tutorialId={activeTutorialId}
      />
    );
  }

  // Don't render if tutorial is not in progress
  if (status !== 'in_progress') {
    return null;
  }

  // Don't render if no step configuration
  if (!currentStepConfig || !displayStepConfig) {
    return null;
  }

  return (
    <>
      {/* Spotlight overlay - hidden for informational steps or when on wrong route */}
      {!displayStepConfig.hideSpotlight && (
        <TutorialSpotlight targetRect={targetRect} />
      )}

      {/* Tooltip with instructions (shows redirect message when on wrong route) */}
      <TutorialTooltip
        step={displayStepConfig}
        stepNumber={currentStep}
        totalSteps={totalSteps}
        targetRect={isOnWrongRoute ? null : targetRect}
        onSkip={handleSkip}
        onExit={handleExit}
        onContinue={handleContinue}
      />
    </>
  );
};

export default TutorialOverlay;
